import React, { useMemo, useState, useEffect } from "react";
import "../../assets/styles/createproduct.css";
import Select from "react-select";
import countryList from "react-select-country-list";
import { useLocation, useNavigate } from "react-router-dom";
import { useStoreDistributor } from "../../hooks/distributor/distributor.store"
import { useStoreDistributorUser } from "../../hooks/distributor/distributor.user.store";
import toast from "react-hot-toast";

const AddDistributors = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize mutation hooks
  const storeDistributor = useStoreDistributor();
  const storeDistributorUser = useStoreDistributorUser();

  // Step management
  const [currentStep, setCurrentStep] = useState(1); // 1 = Distributor, 2 = Users
  const [createdDistributorId, setCreatedDistributorId] = useState(null);
  const [createdDistributorName, setCreatedDistributorName] = useState("");

  // Image states
  const [imagePreview, setImagePreview] = useState();
  const [logoFile, setLogoFile] = useState(null);

  const readURL = (input) => {
    if (input.files && input.files[0]) {
      const file = input.files[0];
      setLogoFile(file); // Store the actual file

      const reader = new FileReader();
      reader.onload = function (e) {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Distributor form state
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [country, setCountry] = useState(null);
  const [status, setStatus] = useState("active");
  const [otpVerificationDays, setOtpVerificationDays] = useState(14);

  // Current user form state (for adding individual users)
  const [currentUser, setCurrentUser] = useState({
    profilePicture: null,
    profilePicturePreview: null,
    email: "",
    phone: "",
    contactPerson: "",
    designation: "",
    country: null,
    password: "",
    verificationIntervalDays: 60
  });

  const options = useMemo(() => countryList().getData(), []);

  // Handle distributor creation
  const handleCreateDistributor = async (e) => {
    e.preventDefault();

    if (!companyName || !website || !country) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      // Prepare FormData for API call
      const formData = new FormData();
      formData.append('companyName', companyName);
      formData.append('website', website);
      formData.append('country', country.label);
      formData.append('accessStatus', status);

      if (logoFile) {
        formData.append('logo', logoFile);
      }

      // Call API to create distributor
      const response = await storeDistributor.mutateAsync(formData);
      console.log("Distributor created:", response);

      // Extract distributor ID from response
      const distributorId = response?.data?.data?._id || response?.data?.id;

      setCreatedDistributorId(distributorId);
      setCreatedDistributorName(companyName);

      // Move to step 2 (Users form)
      setCurrentStep(2);

      toast.success(`Distributor "${companyName}" created successfully! Now you can add a user.`);
    } catch (error) {
      console.error("Error creating distributor:", error);
      toast.error(error?.response?.data?.message || "Failed to create distributor. Please try again.");
    }
  };

  // Handle final submission (create one user for the distributor)
  const handleFinalSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser.email || !currentUser.phone || !currentUser.contactPerson || !currentUser.password) {
      toast.error("Please fill in all required user fields");
      return;
    }

    try {
      // Validate distributor ID
      if (!createdDistributorId) {
        toast.error("Distributor ID is missing. Please create the distributor first.");
        return;
      }

      const formData = new FormData();
      formData.append('companyName', createdDistributorId);
      formData.append('email', currentUser.email);
      formData.append('phone', currentUser.phone);
      formData.append('contactPerson', currentUser.contactPerson);
      formData.append('designation', currentUser.designation || '');
      formData.append('country', currentUser.country?.label || '');
      formData.append('loginAuth[password]', currentUser.password);
      formData.append('otpVerification[verificationIntervalDays]', currentUser.otpVerificationDays)

      if (currentUser.profilePicture) {
        formData.append('profilePicture', currentUser.profilePicture);
      }

      await storeDistributorUser.mutateAsync(formData);

      toast.success(`User added successfully to ${createdDistributorName}!`);
      navigate("/distributorsList");
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error(error?.response?.data?.message || "Failed to create user. Please try again.");
    }
  };

  // Handle skipping user creation
  const handleSkipUsers = () => {
    if (window.confirm(`Are you sure you want to skip adding users to "${createdDistributorName}"? You can add them later.`)) {
      navigate("/distributorsList");
    }
  };

  // Read user profile picture
  const readUserProfilePic = (input) => {
    if (input.files && input.files[0]) {
      const reader = new FileReader();
      reader.onload = function (e) {
        setCurrentUser({
          ...currentUser,
          profilePicture: input.files[0],
          profilePicturePreview: e.target.result
        });
      };
      reader.readAsDataURL(input.files[0]);
    }
  };

  return (
    <>
      <div className="distribute">
        <div className="body">
          <div className="inputFields">

            {/* Step Progress Indicator */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '30px',
              marginBottom: '30px',
              padding: '20px 0',
              borderBottom: '2px solid #eee'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '45px',
                  height: '45px',
                  borderRadius: '50%',
                  background: currentStep === 1 ? '#004996' : '#28a745',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '18px'
                }}>
                  {currentStep === 1 ? '1' : '✓'}
                </div>
                <span style={{
                  fontWeight: currentStep === 1 ? 'bold' : 'normal',
                  fontSize: '16px',
                  color: currentStep === 1 ? '#004996' : '#28a745'
                }}>
                  Create Distributor
                </span>
              </div>

              <div style={{
                width: '80px',
                height: '3px',
                background: currentStep === 2 ? '#004996' : '#ddd',
                borderRadius: '2px'
              }}></div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '45px',
                  height: '45px',
                  borderRadius: '50%',
                  background: currentStep === 2 ? '#004996' : '#ddd',
                  color: currentStep === 2 ? '#fff' : '#999',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '18px'
                }}>
                  2
                </div>
                <span style={{
                  fontWeight: currentStep === 2 ? 'bold' : 'normal',
                  fontSize: '16px',
                  color: currentStep === 2 ? '#004996' : '#999'
                }}>
                  Add Users
                </span>
              </div>
            </div>

            {/* STEP 1: Create Distributor */}
            {currentStep === 1 && (
              <div className="distributors">
                <form onSubmit={handleCreateDistributor}>
                  <div className="createNew" style={{ marginBottom: '30px' }}>
                    <h2 style={{ margin: 0, color: '#004996' }}>Create New Distributor</h2>
                    <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                      Fill in the distributor details below
                    </p>
                  </div>

                  <div className="form-headerr">
                    <div className="image-upload-section">
                      <label style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>
                        Upload Logo:
                      </label>
                      <div className="avatar-upload" style={{ margin: "0" }}>
                        <div className="avatar-edit">
                          <input
                            type="file"
                            id="imageUpload"
                            accept=".png, .jpg, .jpeg"
                            onChange={(e) => readURL(e.target)}
                          />
                          <label htmlFor="imageUpload"></label>
                        </div>
                        <div className="avatar-preview">
                          <div
                            id="imagePreview"
                            style={{ backgroundImage: `url(${imagePreview})` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    <div className="name">
                      <label htmlFor="" style={{ fontWeight: 'bold' }}>
                        Company Name <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Enter company name..."
                        required
                        style={{ width: "100%", marginTop: "10px", padding: "12px" }}
                      />
                    </div>

                    <div className="name">
                      <label htmlFor="" style={{ fontWeight: 'bold' }}>
                        Email <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter company email..."
                        required
                        style={{ width: "100%", marginTop: "10px", padding: "12px" }}
                      />
                    </div>

                    <div className="name">
                      <label htmlFor="" style={{ fontWeight: 'bold' }}>
                        Website <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        placeholder="Enter website (e.g., www.company.com)..."
                        required
                        style={{ width: "100%", marginTop: "10px", padding: "12px" }}
                      />
                    </div>

                    <div className="name">
                      <label htmlFor="" style={{ fontWeight: 'bold' }}>
                        Country <span style={{ color: 'red' }}>*</span>
                      </label>
                      <Select
                        options={options}
                        value={country}
                        onChange={setCountry}
                        placeholder="Select country..."
                        required
                        styles={{
                          control: (base) => ({
                            ...base,
                            marginTop: "10px",
                            padding: "4px"
                          }),
                        }}
                      />
                    </div>

                    {/* <div className="name">
                      <label htmlFor="" style={{ fontWeight: 'bold' }}>
                        OTP Verification Days
                      </label>
                      <input
                        type="number"
                        value={otpVerificationDays}
                        onChange={(e) => setOtpVerificationDays(e.target.value)}
                        placeholder="Enter number of days..."
                        min="1"
                        style={{ width: "100%", marginTop: "10px", padding: "12px" }}
                      />
                      <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                        Number of days before OTP verification is required
                      </small>
                    </div> */}

                    <div className="container">
                      <label htmlFor="" style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>
                        Access Status
                      </label>
                      <div className="radioBtns">
                        <div className="radio">
                          <input
                            id="radio-1"
                            name="radio"
                            type="radio"
                            value="active"
                            checked={status === "active"}
                            onChange={(e) => setStatus(e.target.value)}
                          />
                          <label htmlFor="radio-1" className="radio-label">
                            Active
                          </label>
                        </div>

                        <div className="radio">
                          <input
                            id="radio-2"
                            name="radio"
                            type="radio"
                            value="inactive"
                            checked={status === "inactive"}
                            onChange={(e) => setStatus(e.target.value)}
                          />
                          <label htmlFor="radio-2" className="radio-label">
                            Inactive
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '30px' }}>
                    <button
                      type="button"
                      className="commonButton"
                      style={{ background: '#6c757d' }}
                      onClick={() => navigate("/distributorsList")}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="commonButton"
                      style={{ background: '#004996' }}
                    >
                      Create & Add User →
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* STEP 2: Add Users */}
            {currentStep === 2 && (
              <div className="usersRegister">
                <div className="createNew" style={{ marginBottom: '20px' }}>
                  <h2 style={{ margin: 0, color: '#004996' }}>
                    Add User to {createdDistributorName}
                  </h2>
                  <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                    Add one user who will have access to this distributor account
                  </p>
                </div>

                <form onSubmit={handleFinalSubmit}>
                  <div style={{
                    background: '#fff',
                    padding: '25px',
                    borderRadius: '8px',
                    border: '2px dashed #004996',
                    marginBottom: '25px'
                  }}>
                    <h3 style={{ marginTop: 0, color: '#004996' }}>
                      Add User Details
                    </h3>

                    <div className="form-headerr">
                      <div className="image-upload-section">
                        <label style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>
                          Upload Profile Picture:
                        </label>
                        <div className="avatar-upload" style={{ margin: "0" }}>
                          <div className="avatar-edit">
                            <input
                              type="file"
                              id="userImageUpload"
                              accept=".png, .jpg, .jpeg"
                              onChange={(e) => readUserProfilePic(e.target)}
                            />
                            <label htmlFor="userImageUpload"></label>
                          </div>
                          <div className="avatar-preview">
                            <div
                              id="userImagePreview"
                              style={{ backgroundImage: `url(${currentUser.profilePicturePreview})` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      <div className="name">
                        <label htmlFor="" style={{ fontWeight: 'bold' }}>
                          Contact Person Name <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                          type="text"
                          value={currentUser.contactPerson}
                          onChange={(e) => setCurrentUser({ ...currentUser, contactPerson: e.target.value })}
                          placeholder="Enter full name..."
                          required
                          style={{ width: "100%", marginTop: "10px", padding: "12px" }}
                        />
                      </div>

                      <div className="name">
                        <label htmlFor="" style={{ fontWeight: 'bold' }}>
                          Email <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                          type="email"
                          value={currentUser.email}
                          onChange={(e) => setCurrentUser({ ...currentUser, email: e.target.value })}
                          placeholder="Enter email address..."
                          required
                          style={{ width: "100%", marginTop: "10px", padding: "12px" }}
                        />
                      </div>

                      <div className="name">
                        <label htmlFor="" style={{ fontWeight: 'bold' }}>
                          Phone <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                          type="tel"
                          value={currentUser.phone}
                          onChange={(e) => setCurrentUser({ ...currentUser, phone: e.target.value })}
                          placeholder="Enter phone number..."
                          required
                          style={{ width: "100%", marginTop: "10px", padding: "12px" }}
                        />
                      </div>

                      <div className="name">
                        <label htmlFor="" style={{ fontWeight: 'bold' }}>
                          Password <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                          type="password"
                          value={currentUser.password}
                          onChange={(e) => setCurrentUser({ ...currentUser, password: e.target.value })}
                          placeholder="Enter login password..."
                          required
                          style={{ width: "100%", marginTop: "10px", padding: "12px" }}
                        />
                      </div>

                      <div className="name">
                        <label htmlFor="" style={{ fontWeight: 'bold' }}>
                          Designation
                        </label>
                        <input
                          type="text"
                          value={currentUser.designation}
                          onChange={(e) => setCurrentUser({ ...currentUser, designation: e.target.value })}
                          placeholder="Enter job title/designation..."
                          style={{ width: "100%", marginTop: "10px", padding: "12px" }}
                        />
                      </div>

                      <div className="name">
                        <label htmlFor="" style={{ fontWeight: 'bold' }}>
                          Country
                        </label>
                        <Select
                          options={options}
                          value={currentUser.country}
                          onChange={(value) => setCurrentUser({ ...currentUser, country: value })}
                          placeholder="Select country..."
                          styles={{
                            control: (base) => ({
                              ...base,
                              marginTop: "10px",
                              padding: "4px"
                            }),
                          }}
                        />
                      </div>
                      <div className="name">
                        <label htmlFor="" style={{ fontWeight: 'bold' }}>
                          OTP Verification Days
                        </label>
                        <input
                          type="number"
                          value={currentUser.verificationIntervalDays}
                          onChange={(e) => setCurrentUser({ ...currentUser, verificationIntervalDays: e.target.value })}
                          placeholder="Enter number of days..."
                          min="1"
                          style={{ width: "100%", marginTop: "10px", padding: "12px" }}
                        />
                        <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                          Number of days before OTP verification is required
                        </small>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      gap: '15px',
                      justifyContent: 'space-between',
                      paddingTop: '20px',
                      borderTop: '2px solid #eee',
                      marginTop: '20px'
                    }}>
                      <button
                        type="button"
                        className="commonButton"
                        style={{ background: '#6c757d' }}
                        onClick={() => setCurrentStep(1)}
                      >
                        ← Back to Distributor
                      </button>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          type="button"
                          className="commonButton"
                          style={{ background: '#ffc107', color: '#000' }}
                          onClick={handleSkipUsers}
                        >
                          Skip & Finish Later
                        </button>
                        <button
                          type="submit"
                          className="commonButton"
                          style={{ background: '#28a745' }}
                          disabled={storeDistributorUser.isPending}
                        >
                          {storeDistributorUser.isPending ? 'Saving User...' : 'Finish & Save User'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AddDistributors;
